import { createRef, useEffect, useMemo, useState } from "react";
import type Keycloak from "keycloak-js";
import { Button, CssBaseline, StyledEngineProvider } from "@mui/material";
import { PaletteMode, ThemeOptions, ThemeProvider, createTheme } from "@mui/material/styles";
import { ReactKeycloakProvider } from "@react-keycloak/web/lib/provider";
import { SnackbarKey, SnackbarProvider } from "notistack";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import store from "./app/store";
import SnackbarRunner from "./app/snackbar-runner.component";
import { toValidMuiColor } from "./utils/theme-color.utils";
import { DEFAULT_BRAND_COLOR } from "./themes/seedit-theme.tokens";
import { makeSeeditThemeOptions } from "./themes/make-seedit-theme";
import { configureMobiscrollTheme } from "./themes/sync-mobiscroll-theme";
import { initAssets } from "./components/FacilityManager/Functions/initAssets";
import KeycloakReadyComponent from "./components/KeycloakReady/keycloak-ready.component";
import "./components/Mobiscroll/theme.scss";
import AppRouter from "./components/Routing/app-router.component";
import { authenticatedKeycloakInitOptions } from "./features/Login/keycloak-init-options";
import getKeycloakInstance from "./features/Login/functions/keycloak";
import { PublicVisitorConsentBootstrap } from "./public-visitor-consent-bootstrap";
import { isVisitorConsentPublicPath } from "./utils/visitor-consent-public-route.utils";
import "./i18n";
import "./index.css";

// This must happen early to assure they are available when using the fm
initAssets();

const queryClient = new QueryClient();

let keycloakReady = false;

const SnackbarAction = (
  key: SnackbarKey,
  onClickDismiss: (key: SnackbarKey) => () => void,
  t: (key: string) => string
) => (
  <Button
    size="small"
    style={{ color: "white", pointerEvents: "auto" }}
    variant={"text"}
    onClick={onClickDismiss(key)}
  >
    {t("Got it")}
  </Button>
);

export const Index = () => {
  const { t } = useTranslation();
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [keycloakLoading, setKeycloakLoading] = useState(true);

  // notistack part
  const notistackRef = createRef<SnackbarProvider>();
  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  // Load Keycloak instance
  useEffect(() => {
    getKeycloakInstance().then(instance => {
      setKeycloak(instance);
      setKeycloakLoading(false);
    });
  }, []);

  // mui theme part
  const [brandColor, setBrandColor] = useState(DEFAULT_BRAND_COLOR);
  const [colorMode, setColorMode] = useState<PaletteMode>("dark");

  const colorObject: ThemeOptions = useMemo(
    () => makeSeeditThemeOptions(colorMode, toValidMuiColor(brandColor)),
    [brandColor, colorMode]
  );

  const [currentTheme, setCurrentTheme] = useState<ThemeOptions>(colorObject);

  useEffect(() => {
    configureMobiscrollTheme(colorMode, brandColor);
    setCurrentTheme(colorObject);
  }, [brandColor, colorMode, colorObject]);

  // Show loading spinner while Keycloak is being initialized
  if (keycloakLoading || !keycloak) {
    return (
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={createTheme(currentTheme)}>
              <SnackbarProvider
                ref={notistackRef}
                action={key => SnackbarAction(key, onClickDismiss, t)}
                maxSnack={3}
              >
                <SnackbarRunner />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh"
                  }}
                >
                  <div>{t("Loading...")}</div>
                </div>
              </SnackbarProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </QueryClientProvider>
      </Provider>
    );
  }

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          {/* <CacheProvider value={muiCache}> */}
          <ThemeProvider theme={createTheme(currentTheme)}>
            <SnackbarProvider
              ref={notistackRef}
              action={key => SnackbarAction(key, onClickDismiss, t)}
              maxSnack={3}
            >
              <SnackbarRunner />
              <ReactKeycloakProvider
                authClient={keycloak}
                initOptions={authenticatedKeycloakInitOptions}
                onEvent={onKeycloakEvent}
              >
                <KeycloakReadyComponent keycloakReady={keycloakReady}>
                  <CssBaseline />
                  <AppRouter
                    themeColor={colorMode}
                    setBrandColor={setBrandColor}
                    setColorMode={setColorMode}
                    setTheme={setCurrentTheme}
                  />
                </KeycloakReadyComponent>
              </ReactKeycloakProvider>
            </SnackbarProvider>
          </ThemeProvider>
          {/* </CacheProvider> */}
        </StyledEngineProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export const onKeycloakEvent = (event?: string) => {
  if (event && event === "onReady") {
    keycloakReady = true;
  }
};

// https://stackoverflow.com/questions/70355760/keycloak-acts-as-if-its-uninitialized-after-a-hard-page-refresh
export const applyKeycloakRaceConditionFix = () => {
  const renderIfKeycloakReady = () => {
    if (keycloakReady) {
      // rerender to apply changed keycloak ready flag
      root.render(<Index />);
    } else {
      setTimeout(() => {
        renderIfKeycloakReady();
      }, 1000);
    }
  };
  renderIfKeycloakReady();
};

const container = document.getElementById("root");
const root = createRoot(container as HTMLElement);

const bootstrapApp = () => {
  if (isVisitorConsentPublicPath(window.location.pathname)) {
    root.render(<PublicVisitorConsentBootstrap />);
    return;
  }

  root.render(<Index />);
  applyKeycloakRaceConditionFix();
};

bootstrapApp();
